import { colors } from '@globals/themes/defaultTheme'
import { onActive, onDisabled, onError, onFocus, onHover } from '@globals/themes/themeConstants'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import MuiTextAreaAutosize from '@mui/material/TextareaAutosize'
import Typography from '@mui/material/Typography'
import { SxProps, styled } from '@mui/material/styles'
import { T } from '@transifex/react'
import { ReactNode } from 'react'
import { Control, Controller, FieldValues, Path } from 'react-hook-form'
import ErrorText from './ErrorText'
import FieldLabel from './FieldLabel'
import HelpText from './HelpText'

const MAX_CHARS = 150

export const MuiTextAreaStyled = styled(MuiTextAreaAutosize)(({ theme }) => ({
  backgroundColor: colors.white,
  border: `1px solid ${colors.grayExtraLight}`,
  borderRadius: theme.spacing(1),
  boxSizing: 'border-box',
  font: 'inherit',
  padding: '8px 16px',
  width: '100%',
  minHeight: 0,
  resize: 'vertical',
  [onHover]: {
    border: `1px solid ${colors.blueMedium}`,
  },
  [onFocus]: {
    outline: 'none',
  },
  [onActive]: {
    border: `1px solid ${colors.bluePrimary}`,
  },
  [onDisabled]: {
    border: `1px solid ${colors.grayExtraLight}`,
  },
  [onError]: {
    border: `1px solid ${colors.redPrimary}`,
  },
  '&::placeholder': {
    color: colors.grayMedium,
    opacity: 1,
  },
}))

export type TextAreaFieldProps<T extends FieldValues> = {
  name: Path<T>
  control: Control<T>
  label: ReactNode
  placeholder?: string
  required?: boolean
  help?: string
  disabled?: boolean
  onBlur?: () => void
  labelStyle?: SxProps
  maxChars?: number
  hideCounter?: boolean
  minRows?: number
  maxRows?: number
  /**
   * When provided, this node is rendered beneath the textarea (left of the
   * character counter) as the "refine with AI" affordance. Each entity
   * (nomination, etc.) supplies its own button so this field stays decoupled
   * from entity-specific logic — same slot pattern as RichTextEditorField.
   */
  refineButton?: ReactNode
}

const TextAreaField = <T extends FieldValues>({
  control,
  label,
  name,
  help,
  placeholder,
  required = true,
  disabled = false,
  onBlur,
  labelStyle,
  maxChars = MAX_CHARS,
  hideCounter = false,
  minRows = 3,
  maxRows = 16,
  refineButton,
}: TextAreaFieldProps<T>) => {
  return (
    <Box sx={{ width: '100%' }}>
      <Controller
        name={name}
        control={control}
        render={({
          field: { onChange, value, onBlur: onControllerBlur, ref },
          fieldState: { error },
        }) => (
          <>
            <FieldLabel
              htmlFor={name}
              required={required}
              disabled={disabled}
              labelStyle={labelStyle}>
              {label}
            </FieldLabel>
            {help && <HelpText disabled={disabled}>{help}</HelpText>}
            <MuiTextAreaStyled
              onBlur={() => {
                onBlur && onBlur()
                onControllerBlur()
              }}
              maxRows={maxRows}
              placeholder={placeholder}
              disabled={disabled}
              minRows={minRows}
              ref={ref}
              id={name}
              value={value || ''}
              onChange={e => {
                onChange(e)
              }}
              name={name}
              sx={error ? { border: `1px solid ${colors.redPrimary}` } : undefined}
            />
            <Stack spacing={0.5}>
              {error && (
                <Box mt={!hideCounter && !refineButton ? -3.5 : 0} mr={!hideCounter ? 8 : 0}>
                  <ErrorText>{error.message}</ErrorText>
                </Box>
              )}
              {(refineButton || !hideCounter) && (
                <Stack direction="row" alignItems="center">
                  <Box ml={1}>{refineButton}</Box>
                  {!hideCounter && (
                    <Typography variant="caption" sx={{ ml: 'auto' }}>
                      <T
                        _str="{count}/{maxChars}"
                        count={value?.length.toLocaleString() || 0}
                        maxChars={maxChars.toLocaleString()}
                      />
                    </Typography>
                  )}
                </Stack>
              )}
            </Stack>
          </>
        )}
      />
    </Box>
  )
}

export default TextAreaField
